import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Container,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  FormHelperText,
  Autocomplete,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  alpha,
} from '@mui/material'
import {
  FactCheck as FactCheckIcon,
  Description as DescriptionIcon,
  MenuBook as MenuBookIcon,
  Tune as TuneIcon,
  HealthAndSafety as HealthIcon,
  VisibilityOutlined,
  VisibilityOffOutlined,
} from '@mui/icons-material'
import { poolwerxColors } from '../../theme/colors'
import { apiClient } from '../../services/api'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import AdminFormActions from '../../components/admin/shared/AdminFormActions'
import AdminFormCard from '../../components/admin/shared/AdminFormCard'
import type { AdminFormTab } from '../../components/admin/shared/AdminFormTabHeader'
import FormErrorSummary from '../../components/admin/shared/FormErrorSummary'
import { useFormTabs } from '../../hooks/useFormTabs'
import { firstErrorTab } from '../../utils/formErrors'
import { FormTabPanel } from '../../components/shared/FormTabPanel'
import {
  AdminFormSection,
  ChipMultiSelect,
  FieldLabel,
  SegmentedSelector,
  TagInput,
  ToggleGatedSection,
} from '../../components/admin/shared/fields'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import {
  fetchAvailableFunctions,
  fetchDataDomains,
  fetchDqTeamMembers,
} from '../../services/admin/exceptionOptions'
import type { DqTeamMember } from '../../services/admin/exceptionOptions'
import { EXCEPTION_TYPE_META, BUSINESS_AREA_META } from '../../components/SystemIcons'
import ReferenceLinksEditor from '../../components/shared/referenceLinks/ReferenceLinksEditor'
import type { ReferenceLinkInput } from '../../components/shared/referenceLinks/types'
import {
  IMPACT_DESCRIPTIONS,
  IMPACT_OPTIONS,
  SAMPLE_WINDOW_OPTIONS,
  WEIGHT_BY_BUCKET,
  bucketFromWeight,
  sampleFromWindow,
  thresholdBands,
  windowFromSample,
  type ImpactBucket,
  type SampleWindow,
} from './healthScoringMap'

interface ExceptionTypeFormData {
  code: string
  data_domain: string
  exception_type: string
  business_area: string
  name_short: string
  name: string
  overview: string
  why_flagged: string
  search_keywords: string[]
  links: ReferenceLinkInput[]
  is_active: boolean
  markets: string[]
  visible_to_roles: string[]
  review_cadence: string
  dq_owner_user_id: number | null
  included_in_score: boolean
  sample_type: string
  sample_value: string
  measure_type: string
  good_threshold: string
  watch_threshold: string
  poor_threshold: string
  weight: string
  function_name: string
}

const initialFormData: ExceptionTypeFormData = {
  code: '',
  data_domain: '',
  exception_type: '',
  business_area: '',
  name_short: '',
  name: '',
  overview: '',
  why_flagged: '',
  search_keywords: [],
  links: [],
  is_active: true,
  markets: [],
  visible_to_roles: ['EVO_ADMIN', 'EVO_TEAM'],
  review_cadence: '',
  dq_owner_user_id: null,
  included_in_score: false,
  sample_type: '',
  sample_value: '',
  measure_type: '',
  good_threshold: '',
  watch_threshold: '',
  poor_threshold: '',
  weight: '',
  function_name: '',
}

// Soft wording-length guidance: the counter turns warning-coloured past these
// thresholds but never blocks input (the API caps overview at 255).
const WORDING_WARN_CHARS = 160
const OVERVIEW_MAX_CHARS = 255

// Single source of truth for tab order; `useFormTabs` derives slug ↔ index.
const TAB_SLUGS = ['exception', 'about', 'documentation', 'setup', 'health-scoring'] as const

// Validation-UX maps: which tab each errorable field lives on, its human label
// for the error summary, and the DOM id to focus when its summary item is
// clicked. Kept local to the form (fields differ per form).
const FIELD_TO_TAB: Record<string, number> = {
  name: 0,
  exception_type: 0,
  business_area: 0,
  code: 0,
  data_domain: 0,
  good_threshold: 4,
  watch_threshold: 4,
  poor_threshold: 4,
  sample_value: 4,
}
const FIELD_LABELS: Record<string, string> = {
  name: 'Exception name',
  exception_type: 'Exception Type',
  business_area: 'Business Area',
  code: 'Code',
  data_domain: 'Data Domain',
  good_threshold: 'Good threshold',
  watch_threshold: 'Watch threshold',
  poor_threshold: 'Poor threshold',
  sample_value: 'Sample window (days)',
}
const FIELD_ANCHORS: Record<string, string> = {
  name: 'etf-name',
  exception_type: 'etf-exception_type',
  business_area: 'etf-business_area',
  code: 'etf-code',
  good_threshold: 'etf-good_threshold',
  watch_threshold: 'etf-watch_threshold',
  poor_threshold: 'etf-poor_threshold',
  sample_value: 'etf-sample_value',
}

const MARKET_OPTIONS = [
  { label: 'ANZ', value: 'ANZ' },
  { label: 'USA', value: 'USA' },
]
// Classification dropdowns derive from the shared meta maps (single source).
const EXCEPTION_TYPE_SELECT_OPTIONS = Object.values(EXCEPTION_TYPE_META).map((m) => ({
  label: m.label,
  value: m.value,
}))
const BUSINESS_AREA_SELECT_OPTIONS = Object.values(BUSINESS_AREA_META).map((m) => ({
  label: m.label,
  value: m.value,
}))
const ROLE_OPTIONS = ['EVO_ADMIN', 'EVO_TEAM', 'PSC_TEAM', 'FP_USER'].map((r) => ({
  label: r.replace(/_/g, ' '),
  value: r,
}))
const CADENCE_OPTIONS = ['daily', 'weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly']
const MEASURE_OPTIONS = [
  { label: 'Count', value: 'count' },
  { label: 'Rate', value: 'rate' },
]

/**
 * Compact read-only value cell for locked technical fields (subtle grey well,
 * not an editable input). `mono` renders code-like values in a monospace face.
 */
const ReadOnlyValue: React.FC<{ value: string; mono?: boolean }> = ({ value, mono }) => (
  <Box
    sx={{
      minHeight: 40,
      display: 'flex',
      alignItems: 'center',
      px: 1.5,
      py: 0.75,
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'grey.100',
      color: 'text.primary',
      fontSize: '0.875rem',
      fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : undefined,
      wordBreak: 'break-word',
    }}
  >
    {value}
  </Box>
)

interface FpVisibilitySummaryProps {
  isFpVisible: boolean
  changed: boolean
}

const FpVisibilitySummary: React.FC<FpVisibilitySummaryProps> = ({ isFpVisible, changed }) => {
  const Icon = isFpVisible ? VisibilityOutlined : VisibilityOffOutlined
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.75,
        mt: 1.25,
        px: 1,
        py: 0.75,
        borderRadius: 1,
        backgroundColor: poolwerxColors.tealLight,
        border: 1,
        borderColor: 'rgba(0,127,152,0.35)',
      }}
    >
      <Icon sx={{ fontSize: 16, color: poolwerxColors.teal, mt: 0.25 }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: poolwerxColors.teal }}>
          {isFpVisible ? 'FP Visible' : 'PSC Only'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {isFpVisible
            ? 'Franchise Partners can see this for their own FP only. Affects the Inspector Tool and platform report visibility.'
            : 'Franchise Partners will not see this exception or linked report.'}
        </Typography>
        {changed && (
          <Typography variant="caption" sx={{ color: 'info.main', display: 'block', mt: 0.5 }}>
            Changing FP visibility will alter what FPs see on their inspector and platform reports.
          </Typography>
        )}
      </Box>
    </Box>
  )
}

const ExceptionTypeForm: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEditMode = !!id

  const { tabValue, setTabValue, handleTabChange } = useFormTabs(TAB_SLUGS)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<ExceptionTypeFormData>(initialFormData)
  const [originalFormData, setOriginalFormData] = useState<ExceptionTypeFormData>(initialFormData)
  const [savedFpVisible, setSavedFpVisible] = useState<boolean | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  // Options state for dropdowns
  const [functionOptions, setFunctionOptions] = useState<string[]>([])
  const [domainOptions, setDomainOptions] = useState<string[]>([])
  const [dqMembers, setDqMembers] = useState<DqTeamMember[]>([])
  // Owner as loaded from the response - keeps the picker labelled even when
  // the current owner is no longer in the eligible (active EVO) list.
  const [loadedOwner, setLoadedOwner] = useState<DqTeamMember | null>(null)

  useEffect(() => {
    fetchOptions()
    if (isEditMode) {
      fetchExceptionType()
    }
  }, [id])

  // Suppressed during save so the post-save navigate doesn't race a stale dirty flag.
  const { confirmDiscard } = useUnsavedChangesGuard(formData, originalFormData, {
    suppressed: saving,
  })

  const fetchExceptionType = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiClient.get(`/admin/exception-types/${id}`)
      const data = response.data

      const loaded: ExceptionTypeFormData = {
        code: data.code || '',
        data_domain: data.data_domain || '',
        exception_type: data.exception_type || '',
        business_area: data.business_area || '',
        name_short: data.name_short || '',
        name: data.name || '',
        overview: data.overview || '',
        why_flagged: data.why_flagged || '',
        search_keywords: Array.isArray(data.search_keywords) ? data.search_keywords : [],
        links: Array.isArray(data.links)
          ? data.links.map((l: any, i: number) => ({
              link_type: l.link_type ?? 'fix_guide',
              market: l.market ?? null,
              name: l.name ?? '',
              url: l.url ?? '',
              description: l.description ?? '',
              display_order: l.display_order ?? i,
            }))
          : [],
        is_active: data.is_active ?? true,
        markets: data.markets || [],
        visible_to_roles: data.visible_to_roles || [],
        review_cadence: data.review_cadence || '',
        dq_owner_user_id: data.dq_owner_user_id ?? null,
        // Participation in the health score is gated by good_threshold presence.
        included_in_score: data.good_threshold !== null && data.good_threshold !== undefined,
        sample_type: data.sample_type || '',
        sample_value: data.sample_value?.toString() || '',
        measure_type: data.measure_type || '',
        good_threshold: data.good_threshold?.toString() || '',
        watch_threshold: data.watch_threshold?.toString() || '',
        poor_threshold: data.poor_threshold?.toString() || '',
        weight: data.weight?.toString() || '',
        function_name: data.function_name || '',
      }
      setFormData(loaded)
      setOriginalFormData(loaded)
      if (data.dq_owner_user_id) {
        setLoadedOwner({
          id: data.dq_owner_user_id,
          name: data.dq_owner_name || 'Unknown user',
          email: '',
          role: '',
        })
      }
      setSavedFpVisible(Array.isArray(data.visible_to_roles) && data.visible_to_roles.includes('FP_USER'))
    } catch (err: any) {
      if (err.response?.status === 422 && Array.isArray(err.response?.data?.detail)) {
        const validationError = err.response.data.detail[0]
        setError(validationError.msg || 'Validation error occurred')
      } else {
        setError(err.response?.data?.detail || err.message || 'An error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchOptions = async () => {
    try {
      const [functions, domains, members] = await Promise.all([
        fetchAvailableFunctions(),
        fetchDataDomains(),
        fetchDqTeamMembers(),
      ])

      setFunctionOptions(functions.functions || [])
      setDomainOptions(domains.domains || [])
      setDqMembers(members.members || [])
    } catch (error) {
      console.error('Failed to fetch options:', error)
    }
  }

  const handleInputChange = (field: keyof ExceptionTypeFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [field]: event.target.value })
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: '' })
    }
  }

  // Health Scoring control handlers (translate friendly controls → raw fields)
  const handleParticipationToggle = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      included_in_score: checked,
      // Seed a sensible default weight on opt-in if none set; never on opt-out.
      weight: checked && !prev.weight ? WEIGHT_BY_BUCKET.standard.toString() : prev.weight,
    }))
  }

  const handleImpactChange = (bucket: ImpactBucket) => {
    // Only writes the number when the user picks a bucket - untouched weights
    // never drift.
    setFormData((prev) => ({ ...prev, weight: WEIGHT_BY_BUCKET[bucket].toString() }))
  }

  const handleSampleWindowChange = (window: SampleWindow) => {
    setFormData((prev) => ({ ...prev, ...sampleFromWindow(window, prev.sample_value) }))
  }

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (!formData.code.trim()) errors.code = 'Code is required'
    if (!formData.data_domain) errors.data_domain = 'Data domain is required'
    if (!formData.exception_type) errors.exception_type = 'Exception type is required'
    if (!formData.business_area) errors.business_area = 'Business area is required'
    if (!formData.name.trim()) errors.name = 'Name is required'

    if (formData.included_in_score) {
      const thresholds = ['good_threshold', 'watch_threshold', 'poor_threshold'] as const
      thresholds.forEach((field) => {
        const value = formData[field]
        if (value === '' || isNaN(Number(value))) {
          errors[field] = 'Required when scoring is enabled'
        }
      })
      // Bands must be ascending: good ≤ watch ≤ poor.
      if (!errors.good_threshold && !errors.watch_threshold && !errors.poor_threshold) {
        const g = Number(formData.good_threshold)
        const w = Number(formData.watch_threshold)
        const p = Number(formData.poor_threshold)
        if (!(g <= w && w <= p)) {
          errors.watch_threshold = 'Thresholds must increase: Good ≤ Watch ≤ Poor'
        }
      }
      if (formData.sample_type === 'last_days' && (formData.sample_value === '' || isNaN(Number(formData.sample_value)))) {
        errors.sample_value = 'Enter the number of days'
      }
    }

    setValidationErrors(errors)
    return errors
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      // Clear any stale server error; the summary lists the field-level issues.
      setError(null)
      const tab = firstErrorTab(errors, FIELD_TO_TAB)
      if (tab !== null) setTabValue(tab)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setSaving(true)
    setError(null)

    try {
      const submitData: any = {
        code: formData.code.trim(),
        data_domain: formData.data_domain,
        exception_type: formData.exception_type,
        business_area: formData.business_area,
        name: formData.name.trim(),
        is_active: formData.is_active,
        markets: formData.markets.length > 0 ? formData.markets : null,
        visible_to_roles: formData.visible_to_roles.length > 0 ? formData.visible_to_roles : null,
        // Always send (null clears) so the cadence/owner can be unset.
        review_cadence: formData.review_cadence || null,
        dq_owner_user_id: formData.dq_owner_user_id,
      }

      // name_short / overview / why_flagged: always include on edit (so they
      // can be cleared - a blank why_flagged removes the drawer's closing
      // line); on create only when non-empty.
      if (isEditMode) {
        submitData.name_short = formData.name_short.trim() || null
        submitData.overview = formData.overview.trim() || null
        submitData.why_flagged = formData.why_flagged.trim() || null
      } else {
        if (formData.name_short.trim()) submitData.name_short = formData.name_short.trim()
        if (formData.overview.trim()) submitData.overview = formData.overview.trim()
        if (formData.why_flagged.trim()) submitData.why_flagged = formData.why_flagged.trim()
      }

      const cleanedLinks = formData.links
        .filter((l) => l.name.trim() && l.url.trim())
        .map((l, idx) => ({
          link_type: l.link_type,
          market: l.market,
          name: l.name.trim(),
          url: l.url.trim(),
          description: l.description?.trim() || null,
          display_order: idx,
        }))
      if (isEditMode || cleanedLinks.length > 0) {
        submitData.links = cleanedLinks
      }

      // search_keywords already an array of clean tokens.
      const keywords = formData.search_keywords.map((s) => s.trim()).filter((s) => s.length > 0)
      if (isEditMode) {
        submitData.search_keywords = keywords
      } else if (keywords.length > 0) {
        submitData.search_keywords = keywords
      }

      // Health scoring. Participation is gated by good_threshold presence, so
      // turning it off clears all three thresholds; turning it on sends the
      // configured measurement/impact/threshold values. (weight/measure are
      // deliberately left as-is when off - they're never read once
      // good_threshold is null.)
      if (formData.included_in_score) {
        submitData.good_threshold = parseFloat(formData.good_threshold)
        submitData.watch_threshold = parseFloat(formData.watch_threshold)
        submitData.poor_threshold = parseFloat(formData.poor_threshold)
        if (formData.measure_type) submitData.measure_type = formData.measure_type
        if (formData.sample_type) submitData.sample_type = formData.sample_type
        // Send explicitly (null when blank) so switching to All time / Last 30
        // days clears a stale custom day count rather than leaving it orphaned.
        submitData.sample_value = formData.sample_value ? parseInt(formData.sample_value) : null
        if (formData.weight) submitData.weight = parseInt(formData.weight)
      } else {
        submitData.good_threshold = null
        submitData.watch_threshold = null
        submitData.poor_threshold = null
      }

      // Tagging function: only settable at creation (editing re-tags a
      // different set and orphans lifecycle history), so omit it on edit.
      if (!isEditMode && formData.function_name) {
        submitData.function_name = formData.function_name
      }

      // Snapshot so the unsaved-changes guard doesn't fire on the post-save navigate.
      setOriginalFormData(formData)

      if (isEditMode) {
        await apiClient.put(`/admin/exception-types/${id}`, submitData)
      } else {
        await apiClient.post('/admin/exception-types', submitData)
      }

      navigate('/admin/system/exception-setup')
    } catch (err: any) {
      if (err.response?.status === 422 && Array.isArray(err.response?.data?.detail)) {
        const validationError = err.response.data.detail[0]
        setError(validationError.msg || 'Validation error occurred')
      } else {
        setError(err.response?.data?.detail || err.message || 'An error occurred')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (!confirmDiscard()) return
    navigate('/admin/system/exception-setup')
  }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  const exceptionTypeName = formData.name?.trim()

  // Resolve the Autocomplete value: prefer the live eligible-member record,
  // fall back to the owner loaded from the response.
  const selectedOwner: DqTeamMember | null = formData.dq_owner_user_id
    ? dqMembers.find((m) => m.id === formData.dq_owner_user_id) ?? loadedOwner ?? null
    : null

  const sampleWindow = windowFromSample(formData.sample_type, formData.sample_value)
  const bands = thresholdBands(
    formData.good_threshold,
    formData.watch_threshold,
    formData.poor_threshold,
  )

  // Tabs carrying an outstanding validation error are flagged so a failed
  // submit points at the right tab even when the user is on another one.
  const erroredTabs = new Set(
    Object.entries(validationErrors)
      .filter(([, message]) => Boolean(message))
      .map(([field]) => FIELD_TO_TAB[field])
      .filter((tab) => tab !== undefined),
  )
  const tabs: AdminFormTab[] = [
    { label: 'Exception', icon: <FactCheckIcon />, hasError: erroredTabs.has(0) },
    { label: 'About', icon: <DescriptionIcon />, hasError: erroredTabs.has(1) },
    {
      label: 'Help Docs',
      icon: <MenuBookIcon />,
      count: formData.links.length,
      hasError: erroredTabs.has(2),
    },
    { label: 'Setup', icon: <TuneIcon />, hasError: erroredTabs.has(3) },
    { label: 'Health Scoring', icon: <HealthIcon />, hasError: erroredTabs.has(4) },
  ]

  return (
    <>
      <Container maxWidth="lg">
        <AdminPageHeader
          pageKey="exceptionTypes"
          dense
          titleOverride={isEditMode ? 'Edit Exception' : 'Add Exception'}
          breadcrumbs={[
            { label: 'Admin', to: '/admin' },
            { label: 'System', to: '/admin/system' },
            { label: 'Exception Setup', to: '/admin/system/exception-setup' },
            ...(isEditMode
              ? [{ label: exceptionTypeName || 'Exception' }, { label: 'Edit' }]
              : [{ label: 'New' }]),
          ]}
        />

        <FormErrorSummary
          message={error}
          errors={validationErrors}
          fieldLabels={FIELD_LABELS}
          fieldToTab={FIELD_TO_TAB}
          fieldAnchors={FIELD_ANCHORS}
          onJumpToTab={setTabValue}
        />

        <AdminFormCard tabs={tabs} value={tabValue} onChange={handleTabChange}>
          {/* Exception Tab */}
              <FormTabPanel idPrefix="exception-type" value={tabValue} index={0}>
                <Grid container spacing={3} alignItems="flex-start">
                  <AdminFormSection title="Naming" card gridProps={{ md: 6 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FieldLabel>Exception name *</FieldLabel>
                        <TextField
                          id="etf-name"
                          fullWidth
                          placeholder="Full exception name shown in the UI"
                          value={formData.name}
                          onChange={handleInputChange('name')}
                          error={!!validationErrors.name}
                          helperText={validationErrors.name || 'Full exception name shown in the UI'}
                          required
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FieldLabel>Exception short name</FieldLabel>
                        <TextField
                          fullWidth
                          placeholder="Shortened name (optional)"
                          value={formData.name_short}
                          onChange={handleInputChange('name_short')}
                          helperText="Shortened name for display in lists (defaults to exception name if empty)"
                        />
                      </Grid>
                    </Grid>
                  </AdminFormSection>

                  <AdminFormSection title="Classification" card gridProps={{ md: 6 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FieldLabel>Exception Type *</FieldLabel>
                        <FormControl fullWidth error={!!validationErrors.exception_type}>
                          <Select
                            id="etf-exception_type"
                            displayEmpty
                            value={formData.exception_type}
                            onChange={(e) =>
                              setFormData({ ...formData, exception_type: e.target.value })
                            }
                            required
                          >
                            <MenuItem value="" disabled>
                              <em>Select a type</em>
                            </MenuItem>
                            {EXCEPTION_TYPE_SELECT_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {validationErrors.exception_type ||
                              'Is this a process/compliance issue or a data discrepancy?'}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FieldLabel>Business Area *</FieldLabel>
                        <FormControl fullWidth error={!!validationErrors.business_area}>
                          <Select
                            id="etf-business_area"
                            displayEmpty
                            value={formData.business_area}
                            onChange={(e) =>
                              setFormData({ ...formData, business_area: e.target.value })
                            }
                            required
                          >
                            <MenuItem value="" disabled>
                              <em>Select an area</em>
                            </MenuItem>
                            {BUSINESS_AREA_SELECT_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {validationErrors.business_area ||
                              'The department responsible for the change in behaviour'}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AdminFormSection>

                  <AdminFormSection
                    title="System Details"
                    card
                    caption="Technical fields - data domain, code and tagging function are set when the exception is created and are read-only here."
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <FieldLabel>{isEditMode ? 'Data Domain' : 'Data Domain *'}</FieldLabel>
                        {isEditMode ? (
                          <ReadOnlyValue value={formData.data_domain || '-'} />
                        ) : (
                          <FormControl fullWidth error={!!validationErrors.data_domain}>
                            <Select
                              displayEmpty
                              value={formData.data_domain}
                              onChange={(e) => setFormData({ ...formData, data_domain: e.target.value })}
                              required
                            >
                              {domainOptions.map((domain) => (
                                <MenuItem key={domain} value={domain}>
                                  {domain}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              {validationErrors.data_domain || 'The data domain this exception belongs to'}
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FieldLabel>{isEditMode ? 'Code' : 'Code *'}</FieldLabel>
                        {isEditMode ? (
                          <ReadOnlyValue value={formData.code} mono />
                        ) : (
                          <TextField
                            id="etf-code"
                            fullWidth
                            placeholder="e.g. ex-inv-ls-avg-cost-zero"
                            value={formData.code}
                            onChange={handleInputChange('code')}
                            error={!!validationErrors.code}
                            helperText={validationErrors.code || 'Unique identifier (e.g., ex-inv-ls-avg-cost-zero)'}
                            required
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FieldLabel>Tagging Function</FieldLabel>
                        {isEditMode ? (
                          <ReadOnlyValue value={formData.function_name || 'None'} mono />
                        ) : (
                          <FormControl fullWidth>
                            <Select
                              displayEmpty
                              value={formData.function_name}
                              onChange={(e) => setFormData({ ...formData, function_name: e.target.value })}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {functionOptions.map((func) => (
                                <MenuItem key={func} value={func}>
                                  {func}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              The function used to detect and tag this exception type
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Grid>
                    </Grid>
                  </AdminFormSection>
                </Grid>
              </FormTabPanel>

              {/* About Tab */}
              <FormTabPanel idPrefix="exception-type" value={tabValue} index={1}>
                <Grid container spacing={3} alignItems="flex-start">
                  <AdminFormSection title="Exception Wording" card>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FieldLabel>Overview</FieldLabel>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="One-line overview of the exception"
                          value={formData.overview}
                          onChange={handleInputChange('overview')}
                          helperText={`${formData.overview.length}/${OVERVIEW_MAX_CHARS} chars (aim for 10-25 words) - shown under the exception page title and on exception report cards`}
                          FormHelperTextProps={
                            formData.overview.length > WORDING_WARN_CHARS
                              ? { sx: { color: 'warning.main' } }
                              : undefined
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FieldLabel>Why this was flagged</FieldLabel>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Closing copy for the drawer's 'Why this was flagged' section"
                          value={formData.why_flagged}
                          onChange={handleInputChange('why_flagged')}
                          helperText={`${formData.why_flagged.length} chars (aim for 1-2 short sentences, ~${WORDING_WARN_CHARS} max) - shown in the drawer after the computed detail lines; blank shows only the computed lines`}
                          FormHelperTextProps={
                            formData.why_flagged.length > WORDING_WARN_CHARS
                              ? { sx: { color: 'warning.main' } }
                              : undefined
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FieldLabel>Search keywords</FieldLabel>
                        <TagInput
                          value={formData.search_keywords}
                          onChange={(kw) => setFormData({ ...formData, search_keywords: kw })}
                          placeholder="Type a term, press Enter…"
                          helperText="Synonyms users may search by (e.g. invoice, receipt, po). Common aliases (sale↔invoice, po↔purchase order) are handled centrally - add row-specific terms here."
                        />
                      </Grid>
                    </Grid>
                  </AdminFormSection>
                </Grid>
              </FormTabPanel>

              {/* Documentation Tab */}
              <FormTabPanel idPrefix="exception-type" value={tabValue} index={2}>
                <Grid container spacing={3} alignItems="flex-start">
                  <AdminFormSection
                    title="Help & Documentation"
                    card
                    caption='Add per-market Fix Guide URLs surfaced inside the inspector Issues popover. Leave Market on "All markets" if the same link applies everywhere.'
                  >
                    <ReferenceLinksEditor
                      links={formData.links}
                      onChange={(links) => setFormData({ ...formData, links })}
                      helperText=""
                    />
                  </AdminFormSection>
                </Grid>
              </FormTabPanel>

              {/* Setup Tab */}
              <FormTabPanel idPrefix="exception-type" value={tabValue} index={3}>
                <Grid container spacing={3} alignItems="flex-start">
                  {/* Left column: Activation + Targeting */}
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={3}>
                      <AdminFormSection title="Activation" card dense>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                              <Switch
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                              />
                            }
                            label="Active - included in tagging and visible where allowed"
                          />
                          <Chip
                            size="small"
                            label={formData.is_active ? 'Active' : 'Inactive'}
                            sx={
                              formData.is_active
                                ? {
                                    bgcolor: poolwerxColors.tealLight,
                                    color: poolwerxColors.teal,
                                    fontWeight: 600,
                                  }
                                : { fontWeight: 600 }
                            }
                          />
                        </Box>
                      </AdminFormSection>

                      <AdminFormSection title="Targeting" card>
                        <Stack spacing={2}>
                          <Box>
                            <FieldLabel>Markets</FieldLabel>
                            <ChipMultiSelect
                              options={MARKET_OPTIONS}
                              value={formData.markets}
                              onChange={(markets) => setFormData({ ...formData, markets })}
                            />
                          </Box>
                          <Box>
                            <FieldLabel>Visible Roles</FieldLabel>
                            <ChipMultiSelect
                              options={ROLE_OPTIONS}
                              value={formData.visible_to_roles}
                              onChange={(roles) => setFormData({ ...formData, visible_to_roles: roles })}
                            />
                            <FormHelperText>
                              Which roles can see this exception in the inspector. Leave empty to hide from all.
                            </FormHelperText>
                            {formData.visible_to_roles.includes('FP_USER') && (
                              <FpVisibilitySummary
                                isFpVisible
                                changed={savedFpVisible !== null && !savedFpVisible}
                              />
                            )}
                          </Box>
                        </Stack>
                      </AdminFormSection>
                    </Grid>
                  </Grid>

                  {/* Right column: DQ Ownership */}
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={3}>
                      <AdminFormSection title="Data Quality Team Setup" card>
                        <Stack spacing={2}>
                          <Box>
                            <FieldLabel>DQ Owner</FieldLabel>
                            <Autocomplete<DqTeamMember>
                              options={dqMembers}
                              value={selectedOwner}
                              onChange={(_event, newValue) =>
                                setFormData({ ...formData, dq_owner_user_id: newValue ? newValue.id : null })
                              }
                              getOptionLabel={(option) =>
                                option.email ? `${option.name} (${option.email})` : option.name
                              }
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select team member"
                                  size="small"
                                  helperText="Person accountable for this exception type."
                                />
                              )}
                            />
                          </Box>
                          <Box>
                            <FieldLabel>Review Cadence</FieldLabel>
                            <FormControl fullWidth size="small">
                              <Select
                                displayEmpty
                                value={formData.review_cadence}
                                onChange={(e) => setFormData({ ...formData, review_cadence: e.target.value })}
                              >
                                <MenuItem value="">
                                  <em>None (uncadenced)</em>
                                </MenuItem>
                                {CADENCE_OPTIONS.map((c) => (
                                  <MenuItem key={c} value={c}>
                                    {c.charAt(0).toUpperCase() + c.slice(1)}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                Set based on check frequency for the team.
                              </FormHelperText>
                            </FormControl>
                          </Box>
                        </Stack>
                      </AdminFormSection>
                    </Grid>
                  </Grid>
                </Grid>
              </FormTabPanel>

              {/* Health Scoring Tab */}
              <FormTabPanel idPrefix="exception-type" value={tabValue} index={4}>
                <ToggleGatedSection
                  emphasis
                  label="Included in health score"
                  description="When on, this type contributes to FP health scores using the settings below."
                  checked={formData.included_in_score}
                  onChange={handleParticipationToggle}
                  offState={
                    <Alert severity="info" variant="outlined" sx={{ maxWidth: 640 }}>
                      This exception type does not contribute to FP health scores. Turn on to configure
                      measurement, impact and severity thresholds.
                    </Alert>
                  }
                >
                  <Grid container spacing={3} alignItems="flex-start">
                    <AdminFormSection
                      title="Measurement"
                      card
                      gridProps={{ md: 6 }}
                      caption="Choose how this exception is counted for health scoring."
                    >
                      <Stack spacing={2}>
                        <Box>
                          <FieldLabel>Measure by</FieldLabel>
                          <SegmentedSelector
                            options={MEASURE_OPTIONS}
                            value={formData.measure_type}
                            onChange={(v) => setFormData({ ...formData, measure_type: v })}
                            ariaLabel="measure by"
                          />
                        </Box>
                        <Box>
                          <FieldLabel>Sample window</FieldLabel>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                            <SegmentedSelector
                              options={SAMPLE_WINDOW_OPTIONS}
                              value={sampleWindow}
                              onChange={handleSampleWindowChange}
                              ariaLabel="sample window"
                            />
                            {sampleWindow === 'custom' && (
                              <TextField
                                id="etf-sample_value"
                                label="Days"
                                type="number"
                                size="small"
                                value={formData.sample_value}
                                onChange={handleInputChange('sample_value')}
                                error={!!validationErrors.sample_value}
                                helperText={validationErrors.sample_value}
                                sx={{ width: 120 }}
                                inputProps={{ min: 1, max: 365 }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Stack>
                    </AdminFormSection>

                    <AdminFormSection title="Impact" card gridProps={{ md: 6 }}>
                      <FieldLabel>Weight</FieldLabel>
                      <SegmentedSelector
                        options={IMPACT_OPTIONS}
                        value={bucketFromWeight(formData.weight ? Number(formData.weight) : null)}
                        onChange={handleImpactChange}
                        ariaLabel="impact"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Weight value: {formData.weight || WEIGHT_BY_BUCKET.standard}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {IMPACT_DESCRIPTIONS[bucketFromWeight(formData.weight ? Number(formData.weight) : null)]}
                      </Typography>
                    </AdminFormSection>

                    <AdminFormSection title="Severity Thresholds" card>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            id="etf-good_threshold"
                            fullWidth
                            label="Good if value is ≤"
                            value={formData.good_threshold}
                            onChange={handleInputChange('good_threshold')}
                            error={!!validationErrors.good_threshold}
                            helperText={validationErrors.good_threshold}
                            type="number"
                            inputProps={{ step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            id="etf-watch_threshold"
                            fullWidth
                            label="Watch from"
                            value={formData.watch_threshold}
                            onChange={handleInputChange('watch_threshold')}
                            error={!!validationErrors.watch_threshold}
                            helperText={validationErrors.watch_threshold}
                            type="number"
                            inputProps={{ step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            id="etf-poor_threshold"
                            fullWidth
                            label="Poor from"
                            value={formData.poor_threshold}
                            onChange={handleInputChange('poor_threshold')}
                            error={!!validationErrors.poor_threshold}
                            helperText={validationErrors.poor_threshold}
                            type="number"
                            inputProps={{ step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Critical applies when the value is above the Poor range.
                          </Typography>
                        </Grid>
                        {bands.length > 0 && (
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              Preview
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                maxWidth: 480,
                              }}
                            >
                              {bands.map((b, i) => (
                                <Box
                                  key={b.key}
                                  sx={{
                                    flex: 1,
                                    px: 1.25,
                                    py: 0.75,
                                    textAlign: 'center',
                                    bgcolor: (theme) => alpha(theme.palette[b.color].main, 0.12),
                                    borderLeft: i === 0 ? 'none' : '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', color: `${b.color}.dark`, fontWeight: 700 }}
                                  >
                                    {b.label}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', color: `${b.color}.dark` }}
                                  >
                                    {b.range}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </AdminFormSection>
                  </Grid>
                </ToggleGatedSection>
              </FormTabPanel>
        </AdminFormCard>

        <form onSubmit={handleSubmit}>
          <AdminFormActions
            submit
            saveLabel={isEditMode ? 'Save Changes' : 'Create Exception'}
            saving={saving}
            onCancel={handleCancel}
          />
        </form>
      </Container>
    </>
  )
}

export default ExceptionTypeForm
